/** Firestore/Storage operations for supplier bills (see supplierBills.ts for the model). */

import { db, storage } from './firebase';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import type { ProjectPLBucket } from '../types';
import type { PurchaseQbBillStatus } from '../types/purchasing';
import { isPurchaseQbBillStatus } from './purchaseQbBillStatus';
import {
  areAllInstallmentsPaid,
  canTransitionSupplierBill,
  installmentApprovalRequired,
  SUPPLIER_BILL_STATUS_LABELS,
  type SupplierBill,
  type SupplierBillCategory,
  type SupplierBillSource,
  type SupplierBillStatus,
  type SupplierInstallmentApproval,
  type SupplierInstallmentApprovals,
  type SupplierInstallmentPayment,
  type SupplierInstallmentPayments,
  type SupplierPaymentInstallmentTerm,
  type SupplierPaymentTermsSpec,
  type SupplierPaymentTrigger,
  type SupplierInstallmentDocument,
  type SupplierInstallmentDocumentKind,
  reindexInstallmentRecords,
  installmentScheduleTotal,
  releasedInstallmentAmountsChanged,
  releasedInstallmentIds,
  roundMoney,
} from './supplierBills';
import { capSupplierBillDescription } from './supplierBillDescription';
import { removeUndefinedDeep } from './removeUndefinedDeep';
import { ensureCrmStorageAccessBeforeUpload } from './crmStorageAccess';

const BILLS = 'supplierBills';

function toDate(v: unknown): Date | undefined {
  if (!v) return undefined;
  if (v instanceof Date) return v;
  if (typeof (v as { toDate?: () => Date }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate();
  }
  const d = new Date(v as string);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

const DOCUMENT_KINDS: SupplierInstallmentDocumentKind[] = ['statement', 'invoice', 'receipt', 'quote', 'other'];

function mapInstallmentDocuments(raw: unknown): SupplierInstallmentDocument[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const docs: SupplierInstallmentDocument[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const d = item as Record<string, unknown>;
    const kind = d.kind as SupplierInstallmentDocumentKind;
    const fileUrl = typeof d.fileUrl === 'string' ? d.fileUrl.trim() : '';
    const storagePath = typeof d.storagePath === 'string' ? d.storagePath.trim() : '';
    const fileName = typeof d.fileName === 'string' ? d.fileName.trim() : '';
    if (!fileUrl || !storagePath || !fileName) continue;
    if (!DOCUMENT_KINDS.includes(kind)) continue;
    docs.push({
      id: String(d.id || `doc-${docs.length}`),
      kind,
      fileName,
      storagePath,
      fileUrl,
      contentType: typeof d.contentType === 'string' ? d.contentType : undefined,
    });
  }
  return docs.length ? docs : undefined;
}

function mapPaymentTerms(raw: unknown): SupplierPaymentTermsSpec | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const d = raw as Record<string, unknown>;
  if (!Array.isArray(d.installments)) return undefined;
  const installments: SupplierPaymentInstallmentTerm[] = [];
  for (const item of d.installments) {
    if (!item || typeof item !== 'object') continue;
    const i = item as Record<string, unknown>;
    const percent = Number(i.percent);
    const amount = Number(i.amount);
    const hasPercent = Number.isFinite(percent) && percent > 0;
    const hasAmount = Number.isFinite(amount) && amount > 0;
    const trigger = i.trigger as SupplierPaymentTrigger;
    if (!hasPercent && !hasAmount) continue;
    if (trigger !== 'on_approval' && trigger !== 'task_complete' && trigger !== 'fixed_date') continue;
    installments.push({
      id: String(i.id || `inst-${installments.length}`),
      label: typeof i.label === 'string' ? i.label : undefined,
      percent: hasPercent ? percent : 0,
      amount: hasAmount ? roundMoney(amount) : undefined,
      trigger,
      taskId: typeof i.taskId === 'string' ? i.taskId : undefined,
      taskTitle: typeof i.taskTitle === 'string' ? i.taskTitle : undefined,
      netDays: Number.isFinite(Number(i.netDays)) && Number(i.netDays) > 0 ? Math.round(Number(i.netDays)) : undefined,
      fixedDate: toDate(i.fixedDate),
      documents: mapInstallmentDocuments(i.documents),
    });
  }
  if (installments.length === 0) return undefined;
  return {
    installments,
    earlyPayDiscountPercent:
      Number.isFinite(Number(d.earlyPayDiscountPercent)) && Number(d.earlyPayDiscountPercent) > 0
        ? Number(d.earlyPayDiscountPercent)
        : undefined,
    earlyPayWithinDays:
      Number.isFinite(Number(d.earlyPayWithinDays)) && Number(d.earlyPayWithinDays) > 0
        ? Math.round(Number(d.earlyPayWithinDays))
        : undefined,
    notes: typeof d.notes === 'string' && d.notes.trim() ? d.notes.trim() : undefined,
    includesFreight: d.includesFreight === true ? true : undefined,
  };
}

function serializePaymentTerms(terms: SupplierPaymentTermsSpec): Record<string, unknown> {
  return removeUndefinedDeep({
    installments: terms.installments.map(i =>
      removeUndefinedDeep({
        id: i.id,
        label: i.label,
        percent: i.percent,
        amount: i.amount != null && i.amount > 0 ? roundMoney(i.amount) : undefined,
        trigger: i.trigger,
        taskId: i.taskId,
        taskTitle: i.taskTitle,
        netDays: i.netDays,
        fixedDate: i.fixedDate ? Timestamp.fromDate(i.fixedDate) : undefined,
        documents: i.documents?.map((doc) => removeUndefinedDeep({
          id: doc.id,
          kind: doc.kind,
          fileName: doc.fileName,
          storagePath: doc.storagePath,
          fileUrl: doc.fileUrl,
          contentType: doc.contentType,
        })),
      }),
    ),
    earlyPayDiscountPercent: terms.earlyPayDiscountPercent,
    earlyPayWithinDays: terms.earlyPayWithinDays,
    notes: terms.notes?.trim() || undefined,
    includesFreight: terms.includesFreight === true ? true : undefined,
  }) as Record<string, unknown>;
}

function mapInstallmentPayments(raw: unknown): SupplierInstallmentPayments | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const out: SupplierInstallmentPayments = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const idx = Number(key);
    if (!Number.isInteger(idx) || idx < 0) continue;
    if (!value || typeof value !== 'object') continue;
    const p = value as Record<string, unknown>;
    const paidAt = toDate(p.paidAt);
    if (!paidAt) continue;
    out[idx] = {
      paidAt,
      byUserId: String(p.byUserId || ''),
      byUserName: String(p.byUserName || ''),
      note: typeof p.note === 'string' && p.note.trim() ? p.note.trim() : undefined,
      amountPaid: typeof p.amountPaid === 'number' && Number.isFinite(p.amountPaid)
        ? roundMoney(p.amountPaid)
        : undefined,
    };
  }
  return Object.keys(out).length ? out : undefined;
}

function serializeInstallmentPayments(
  payments: SupplierInstallmentPayments,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, p] of Object.entries(payments)) {
    out[key] = removeUndefinedDeep({
      paidAt: Timestamp.fromDate(p.paidAt),
      byUserId: p.byUserId,
      byUserName: p.byUserName,
      note: p.note,
      amountPaid: p.amountPaid != null ? roundMoney(p.amountPaid) : undefined,
    });
  }
  return out;
}

function mapInstallmentApprovals(raw: unknown): SupplierInstallmentApprovals | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const out: SupplierInstallmentApprovals = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const idx = Number(key);
    if (!Number.isInteger(idx) || idx < 0) continue;
    if (!value || typeof value !== 'object') continue;
    const a = value as Record<string, unknown>;
    const approvedAt = toDate(a.approvedAt);
    if (!approvedAt) continue;
    out[idx] = {
      approvedAt,
      byUserId: String(a.byUserId || ''),
      byUserName: String(a.byUserName || ''),
      note: typeof a.note === 'string' && a.note.trim() ? a.note.trim() : undefined,
    };
  }
  return Object.keys(out).length ? out : undefined;
}

function serializeInstallmentApprovals(
  approvals: SupplierInstallmentApprovals,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, a] of Object.entries(approvals)) {
    out[key] = removeUndefinedDeep({
      approvedAt: Timestamp.fromDate(a.approvedAt),
      byUserId: a.byUserId,
      byUserName: a.byUserName,
      note: a.note,
    });
  }
  return out;
}

export function mapBill(snap: { id: string; data: () => Record<string, unknown> }): SupplierBill {
  const d = snap.data();
  const history = Array.isArray(d.statusHistory)
    ? (d.statusHistory as Record<string, unknown>[]).map((h) => ({
        status: (h.status as SupplierBillStatus) || 'pending_review',
        byUserId: String(h.byUserId || ''),
        byUserName: String(h.byUserName || ''),
        at: toDate(h.at) ?? new Date(),
        note: typeof h.note === 'string' ? h.note : undefined,
      }))
    : [];
  return {
    id: snap.id,
    source: (d.source as SupplierBillSource) || 'manual_upload',
    category: (d.category as SupplierBillCategory) || 'other',
    vendorName: String(d.vendorName || ''),
    supplierOrgId: typeof d.supplierOrgId === 'string' ? d.supplierOrgId : undefined,
    rfqProjectId: typeof d.rfqProjectId === 'string' ? d.rfqProjectId : undefined,
    rfqSubmissionId: typeof d.rfqSubmissionId === 'string' ? d.rfqSubmissionId : undefined,
    purchaseRequisitionId: typeof d.purchaseRequisitionId === 'string' ? d.purchaseRequisitionId : undefined,
    poNumber: typeof d.poNumber === 'string' ? d.poNumber : undefined,
    projectFileId: typeof d.projectFileId === 'string' ? d.projectFileId : undefined,
    dealId: typeof d.dealId === 'string' ? d.dealId : undefined,
    projectId: typeof d.projectId === 'string' ? d.projectId : undefined,
    plBucket: typeof d.plBucket === 'string' ? (d.plBucket as ProjectPLBucket) : undefined,
    description: String(d.description || ''),
    amount: typeof d.amount === 'number' ? d.amount : 0,
    dueDate: toDate(d.dueDate),
    fileUrl: typeof d.fileUrl === 'string' ? d.fileUrl : undefined,
    storagePath: typeof d.storagePath === 'string' ? d.storagePath : undefined,
    fileName: typeof d.fileName === 'string' ? d.fileName : undefined,
    status: (d.status as SupplierBillStatus) || 'pending_review',
    qbReferenceNote: typeof d.qbReferenceNote === 'string' ? d.qbReferenceNote : undefined,
    qbBillStatus: isPurchaseQbBillStatus(d.qbBillStatus) ? d.qbBillStatus : undefined,
    qbBillId: typeof d.qbBillId === 'string' ? d.qbBillId : undefined,
    qbBillUrl: typeof d.qbBillUrl === 'string' ? d.qbBillUrl : undefined,
    qbBillMessage: typeof d.qbBillMessage === 'string' ? d.qbBillMessage : undefined,
    paymentTerms: mapPaymentTerms(d.paymentTerms),
    installmentPayments: mapInstallmentPayments(d.installmentPayments),
    amountPaid: typeof d.amountPaid === 'number' && Number.isFinite(d.amountPaid)
      ? roundMoney(d.amountPaid)
      : undefined,
    installmentApprovals: mapInstallmentApprovals(d.installmentApprovals),
    paymentMethod: typeof d.paymentMethod === 'string' ? d.paymentMethod : null,
    qbClearedAt: toDate(d.qbClearedAt) ?? null,
    qbPaymentDepositDate: toDate(d.qbPaymentDepositDate) ?? null,
    statusHistory: history,
    createdByUserId: String(d.createdByUserId || ''),
    createdByUserName: String(d.createdByUserName || ''),
    createdAt: toDate(d.createdAt) ?? new Date(),
    updatedAt: toDate(d.updatedAt) ?? new Date(),
  };
}

export interface CreateSupplierBillInput {
  source: SupplierBillSource;
  category: SupplierBillCategory;
  vendorName: string;
  description: string;
  amount: number;
  status?: SupplierBillStatus;
  supplierOrgId?: string;
  rfqProjectId?: string;
  rfqSubmissionId?: string;
  purchaseRequisitionId?: string;
  poNumber?: string;
  projectFileId?: string;
  dealId?: string;
  projectId?: string;
  plBucket?: ProjectPLBucket;
  dueDate?: Date;
  paymentTerms?: SupplierPaymentTermsSpec;
  fileUrl?: string;
  storagePath?: string;
  fileName?: string;
  qbBillStatus?: PurchaseQbBillStatus;
  qbBillMessage?: string;
  byUserId: string;
  byUserName: string;
}

export async function createSupplierBill(input: CreateSupplierBillInput): Promise<string> {
  const now = Timestamp.now();
  const status = input.status ?? 'pending_review';
  const capped = capSupplierBillDescription(input.description);
  const docData = removeUndefinedDeep({
    source: input.source,
    category: input.category,
    vendorName: input.vendorName.trim(),
    description: capped.description,
    ...(capped.overflow ? { notes: capped.overflow } : {}),
    amount: roundMoney(Number(input.amount) || 0),
    supplierOrgId: input.supplierOrgId,
    rfqProjectId: input.rfqProjectId,
    rfqSubmissionId: input.rfqSubmissionId,
    purchaseRequisitionId: input.purchaseRequisitionId,
    poNumber: input.poNumber,
    projectFileId: input.projectFileId,
    dealId: input.dealId,
    projectId: input.projectId,
    plBucket: input.plBucket,
    dueDate: input.dueDate ? Timestamp.fromDate(input.dueDate) : undefined,
    paymentTerms: input.paymentTerms ? serializePaymentTerms(input.paymentTerms) : undefined,
    fileUrl: input.fileUrl,
    storagePath: input.storagePath,
    fileName: input.fileName,
    qbBillStatus: input.qbBillStatus,
    qbBillMessage: input.qbBillMessage,
    status,
    statusHistory: [
      { status, byUserId: input.byUserId, byUserName: input.byUserName, at: now },
    ],
    createdByUserId: input.byUserId,
    createdByUserName: input.byUserName,
    createdAt: now,
    updatedAt: now,
  });
  const refDoc = await addDoc(collection(db, BILLS), docData);
  return refDoc.id;
}

export async function updateSupplierBillStatus(args: {
  bill: SupplierBill;
  to: SupplierBillStatus;
  byUserId: string;
  byUserName: string;
  qbReferenceNote?: string;
  note?: string;
  /** Cash recorded when a bill with no installment terms is marked paid. */
  amountPaid?: number;
  /** ACH / check / card — Forecast clearing lag until QB bank clear. */
  paymentMethod?: string | null;
}): Promise<void> {
  if (!args.bill.id) throw new Error('Missing bill id');
  if (!canTransitionSupplierBill(args.bill.status, args.to)) {
    throw new Error(
      `Cannot move bill from ${SUPPLIER_BILL_STATUS_LABELS[args.bill.status]} to ${SUPPLIER_BILL_STATUS_LABELS[args.to]}`,
    );
  }
  const now = Timestamp.now();
  const event = removeUndefinedDeep({
    status: args.to,
    byUserId: args.byUserId,
    byUserName: args.byUserName,
    at: now,
    note: args.note,
  });
  const payload: Record<string, unknown> = {
    status: args.to,
    statusHistory: [
      ...args.bill.statusHistory.map((h) =>
        removeUndefinedDeep({ ...h, at: Timestamp.fromDate(h.at) }),
      ),
      event,
    ],
    updatedAt: now,
  };
  if (args.qbReferenceNote !== undefined) {
    payload.qbReferenceNote = args.qbReferenceNote.trim();
  }
  if (args.to === 'paid' && args.amountPaid != null) {
    const amount = roundMoney(args.amountPaid);
    if (!(amount > 0)) throw new Error('Enter the amount paid');
    payload.amountPaid = amount;
  }
  if (args.to === 'paid' && args.paymentMethod !== undefined) {
    payload.paymentMethod = args.paymentMethod ? String(args.paymentMethod).trim() || null : null;
  }
  await updateDoc(doc(db, BILLS, args.bill.id), payload as DocumentData);
  if (args.bill.purchaseRequisitionId) {
    const { syncPurchaseClosedWithSupplierBills } = await import('./purchasingRequisitions');
    await syncPurchaseClosedWithSupplierBills({
      purchaseRequisitionId: args.bill.purchaseRequisitionId,
      actorId: args.byUserId,
      actorName: args.byUserName,
    });
  }
}

export async function updateSupplierBillDetails(args: {
  billId: string;
  dealId?: string | null;
  plBucket?: ProjectPLBucket | null;
  dueDate?: Date | null;
  description?: string;
  amount?: number;
}): Promise<void> {
  const payload: Record<string, unknown> = { updatedAt: Timestamp.now() };
  if (args.dealId !== undefined) payload.dealId = args.dealId || null;
  if (args.plBucket !== undefined) payload.plBucket = args.plBucket || null;
  if (args.dueDate !== undefined) {
    payload.dueDate = args.dueDate ? Timestamp.fromDate(args.dueDate) : null;
  }
  if (args.description !== undefined) {
    const capped = capSupplierBillDescription(args.description);
    payload.description = capped.description;
    if (capped.overflow) payload.notes = capped.overflow;
  }
  if (args.amount !== undefined) payload.amount = roundMoney(Number(args.amount) || 0);
  await updateDoc(doc(db, BILLS, args.billId), payload as DocumentData);
}

/**
 * Mark one payment installment paid (or clear it). When the last installment
 * is paid the bill advances to `paid`; un-marking on a paid bill reverts it
 * to `entered_in_qb` so the remaining payment shows as outstanding.
 */
export async function setSupplierBillInstallmentPayment(args: {
  bill: SupplierBill;
  installmentIndex: number;
  paid: boolean;
  byUserId: string;
  byUserName: string;
  note?: string;
  /** Cash recorded for this installment. Other installments are left unchanged. */
  amountPaid?: number;
}): Promise<void> {
  const { bill } = args;
  if (!bill.id) throw new Error('Missing bill id');
  const terms = bill.paymentTerms;
  if (!terms || !terms.installments[args.installmentIndex]) {
    throw new Error('This bill has no matching payment installment');
  }

  if (
    args.paid &&
    installmentApprovalRequired(terms) &&
    !bill.installmentApprovals?.[args.installmentIndex]
  ) {
    throw new Error('This installment needs approval before it can be marked paid');
  }

  const next: SupplierInstallmentPayments = { ...(bill.installmentPayments ?? {}) };
  if (args.paid) {
    const payment: SupplierInstallmentPayment = {
      paidAt: new Date(),
      byUserId: args.byUserId,
      byUserName: args.byUserName,
      note: args.note,
    };
    if (args.amountPaid != null) {
      const amount = roundMoney(args.amountPaid);
      if (!(amount > 0)) throw new Error('Enter the amount paid');
      payment.amountPaid = amount;
    }
    next[args.installmentIndex] = payment;
  } else {
    delete next[args.installmentIndex];
  }

  const now = Timestamp.now();
  const payload: Record<string, unknown> = {
    installmentPayments: serializeInstallmentPayments(next),
    updatedAt: now,
  };

  const serializedHistory = bill.statusHistory.map((h) =>
    removeUndefinedDeep({ ...h, at: Timestamp.fromDate(h.at) }),
  );
  if (args.paid && bill.status !== 'paid' && areAllInstallmentsPaid(terms, next)) {
    payload.status = 'paid';
    payload.statusHistory = [
      ...serializedHistory,
      {
        status: 'paid',
        byUserId: args.byUserId,
        byUserName: args.byUserName,
        at: now,
        note: `Payment ${args.installmentIndex + 1} of ${terms.installments.length} — final installment paid`,
      },
    ];
  } else if (!args.paid && bill.status === 'paid') {
    payload.status = 'entered_in_qb';
    payload.statusHistory = [
      ...serializedHistory,
      {
        status: 'entered_in_qb',
        byUserId: args.byUserId,
        byUserName: args.byUserName,
        at: now,
        note: `Payment ${args.installmentIndex + 1} un-marked — installment outstanding`,
      },
    ];
  }

  await updateDoc(doc(db, BILLS, bill.id), payload as DocumentData);
  if (
    bill.purchaseRequisitionId
    && (payload.status === 'paid' || payload.status === 'entered_in_qb' || bill.status === 'paid')
  ) {
    const { syncPurchaseClosedWithSupplierBills } = await import('./purchasingRequisitions');
    await syncPurchaseClosedWithSupplierBills({
      purchaseRequisitionId: bill.purchaseRequisitionId,
      actorId: args.byUserId,
      actorName: args.byUserName,
    });
  }
}

/** Adjust when an installment was actually paid (keeps who marked it). */
export async function updateSupplierBillInstallmentPaidDate(args: {
  bill: SupplierBill;
  installmentIndex: number;
  paidAt: Date;
}): Promise<void> {
  const { bill } = args;
  if (!bill.id) throw new Error('Missing bill id');
  const existing = bill.installmentPayments?.[args.installmentIndex];
  if (!existing) throw new Error('This installment has no payment record to adjust');

  const next: SupplierInstallmentPayments = {
    ...(bill.installmentPayments ?? {}),
    [args.installmentIndex]: { ...existing, paidAt: args.paidAt },
  };
  await updateDoc(doc(db, BILLS, bill.id), {
    installmentPayments: serializeInstallmentPayments(next),
    updatedAt: Timestamp.now(),
  } as DocumentData);
}

/**
 * Approve (or un-approve) one installment for payment — the admin/CFO release
 * step on multi-installment bills. Un-approving a paid installment is blocked:
 * the payment record has to be undone first.
 */
export async function setSupplierBillInstallmentApproval(args: {
  bill: SupplierBill;
  installmentIndex: number;
  approved: boolean;
  byUserId: string;
  byUserName: string;
  note?: string;
}): Promise<void> {
  const { bill } = args;
  if (!bill.id) throw new Error('Missing bill id');
  if (!bill.paymentTerms?.installments[args.installmentIndex]) {
    throw new Error('This bill has no matching payment installment');
  }
  if (!args.approved && bill.installmentPayments?.[args.installmentIndex]) {
    throw new Error('This installment is already paid — undo the payment before un-approving');
  }

  const next: SupplierInstallmentApprovals = { ...(bill.installmentApprovals ?? {}) };
  if (args.approved) {
    const approval: SupplierInstallmentApproval = {
      approvedAt: new Date(),
      byUserId: args.byUserId,
      byUserName: args.byUserName,
      note: args.note,
    };
    next[args.installmentIndex] = approval;
  } else {
    delete next[args.installmentIndex];
  }

  const now = Timestamp.now();
  const payload: Record<string, unknown> = {
    installmentApprovals: serializeInstallmentApprovals(next),
    updatedAt: now,
  };

  // Approving an installment implies the bill passed review — advance it so
  // the bill-level status never lags behind a released payment.
  if (args.approved && bill.status === 'pending_review') {
    payload.status = 'ready_to_pay';
    payload.statusHistory = [
      ...bill.statusHistory.map((h) =>
        removeUndefinedDeep({ ...h, at: Timestamp.fromDate(h.at) }),
      ),
      {
        status: 'ready_to_pay',
        byUserId: args.byUserId,
        byUserName: args.byUserName,
        at: now,
        note: `Payment ${args.installmentIndex + 1} approved — bill cleared review`,
      },
    ];
  }

  await updateDoc(doc(db, BILLS, bill.id), payload as DocumentData);
}

/** Edit the QuickBooks bill # / memo shown under the bill status. */
export async function setSupplierBillQbReference(billId: string, note: string): Promise<void> {
  await updateDoc(doc(db, BILLS, billId), {
    qbReferenceNote: note.trim(),
    updatedAt: Timestamp.now(),
  } as DocumentData);
}

export type SetSupplierBillPaymentTermsOptions = {
  /**
   * When set to a real submission id, refuse if the bill is keyed to a different
   * quote (prevents CNC multi-quote terms overwrite).
   */
  expectedRfqSubmissionId?: string;
  /**
   * Allow raising `amount` when the terms schedule total differs from the bill
   * by more than $1. Without this flag the save is blocked.
   */
  allowRaiseBillAmount?: boolean;
  byUserId?: string;
  byUserName?: string;
};

function isOrgAwardSubmissionKey(id: string | undefined | null): boolean {
  const key = (id || '').trim();
  return key.startsWith('org:');
}

/** Set (or clear) the payment breakdown entered at quote approval. */
export async function setSupplierBillPaymentTerms(
  billId: string,
  terms: SupplierPaymentTermsSpec | null,
  options?: SetSupplierBillPaymentTermsOptions,
): Promise<void> {
  const billRef = doc(db, BILLS, billId);
  const snap = await getDoc(billRef);
  const existing = snap.exists()
    ? mapBill({ id: snap.id, data: () => (snap.data() || {}) as Record<string, unknown> })
    : null;
  const raw = snap.exists() ? (snap.data() || {}) as Record<string, unknown> : {};
  const expected = (options?.expectedRfqSubmissionId || '').trim();
  if (
    existing
    && expected
    && !isOrgAwardSubmissionKey(expected)
  ) {
    const billKey = (existing.rfqSubmissionId || '').trim();
    if (billKey && !isOrgAwardSubmissionKey(billKey) && billKey !== expected) {
      throw new Error(
        `This bill belongs to quote ${billKey}, not ${expected}. Create or open the correct quote bill before editing terms.`,
      );
    }
  }

  const now = Timestamp.now();
  const payload: Record<string, unknown> = {
    paymentTerms: terms ? serializePaymentTerms(terms) : null,
    updatedAt: now,
  };

  // Keep prior terms for recovery (CNC overwrite bug).
  if (existing?.paymentTerms) {
    const priorHistory = Array.isArray(raw.termsHistory) ? [...raw.termsHistory] : [];
    priorHistory.push(removeUndefinedDeep({
      paymentTerms: serializePaymentTerms(existing.paymentTerms),
      amount: existing.amount,
      replacedAt: now,
      byUserId: options?.byUserId || null,
      byUserName: options?.byUserName || null,
      replacedBySubmissionId: expected || null,
    }));
    payload.termsHistory = priorHistory;
  }

  if (existing && terms) {
    const previous = existing.paymentTerms?.installments ?? [];
    const lockedIds = new Set(releasedInstallmentIds(existing));
    if (releasedInstallmentAmountsChanged(previous, terms.installments, lockedIds, existing.amount)) {
      throw new Error('An approved or paid installment amount cannot be changed.');
    }
    const nextAmount = installmentScheduleTotal(terms.installments, existing.amount);
    const delta = Math.abs(nextAmount - roundMoney(existing.amount));
    if (delta > 1) {
      if (!options?.allowRaiseBillAmount) {
        throw new Error(
          `Payment terms total $${Math.round(nextAmount).toLocaleString('en-US')} differs from the bill amount $${Math.round(existing.amount).toLocaleString('en-US')} by more than $1. Pass allowRaiseBillAmount to raise the bill, or fix the terms.`,
        );
      }
      payload.amount = nextAmount;
    } else if (delta > 0.009) {
      payload.amount = nextAmount;
    }
    payload.installmentPayments = serializeInstallmentPayments(
      reindexInstallmentRecords(previous, terms.installments, existing.installmentPayments),
    );
    payload.installmentApprovals = serializeInstallmentApprovals(
      reindexInstallmentRecords(previous, terms.installments, existing.installmentApprovals),
    );
  }
  await updateDoc(billRef, payload as DocumentData);
}

export function subscribeSupplierBills(
  cb: (bills: SupplierBill[]) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const q = query(collection(db, BILLS), orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map(mapBill)),
    onError,
  );
}

/** Existing non-void bill for an issued ACH purchase order. */
export async function findSupplierBillForPurchaseOrder(
  purchaseRequisitionId: string,
): Promise<SupplierBill | null> {
  const q = query(
    collection(db, BILLS),
    where('purchaseRequisitionId', '==', purchaseRequisitionId),
  );
  const snap = await getDocs(q);
  const bills = snap.docs.map(mapBill).filter((b) => b.status !== 'void');
  return bills[0] ?? null;
}

export async function stampSupplierBillQbLink(args: {
  bill: SupplierBill;
  qbBillStatus: PurchaseQbBillStatus;
  qbBillId?: string | null;
  qbBillUrl?: string | null;
  qbBillMessage?: string | null;
  qbReferenceNote?: string | null;
  enteredInQb?: boolean;
  byUserId: string;
  byUserName: string;
}): Promise<void> {
  if (!args.bill.id) return;
  const now = Timestamp.now();
  const payload: Record<string, unknown> = {
    qbBillStatus: args.qbBillStatus,
    qbBillId: args.qbBillId?.trim() || null,
    qbBillUrl: args.qbBillUrl?.trim() || null,
    qbBillMessage: args.qbBillMessage?.trim() || null,
    updatedAt: now,
  };
  if (args.qbReferenceNote !== undefined) {
    payload.qbReferenceNote = args.qbReferenceNote?.trim() || null;
  }
  if (args.enteredInQb && args.bill.status === 'ready_to_pay') {
    payload.status = 'entered_in_qb';
    payload.statusHistory = [
      ...args.bill.statusHistory.map((h) =>
        removeUndefinedDeep({ ...h, at: Timestamp.fromDate(h.at) }),
      ),
      removeUndefinedDeep({
        status: 'entered_in_qb',
        byUserId: args.byUserId,
        byUserName: args.byUserName,
        at: now,
        note: args.qbReferenceNote?.trim() || 'QuickBooks bill created from designer invoice PO',
      }),
    ];
  }
  await updateDoc(doc(db, BILLS, args.bill.id), payload as DocumentData);

  const purchaseRequisitionId = (args.bill.purchaseRequisitionId || '').trim();
  const entered =
    args.enteredInQb
    || payload.status === 'entered_in_qb'
    || args.bill.status === 'entered_in_qb';
  if (purchaseRequisitionId && entered) {
    const { syncPurchaseClosedWithSupplierBills } = await import('./purchasingRequisitions');
    await syncPurchaseClosedWithSupplierBills({
      purchaseRequisitionId,
      actorId: args.byUserId,
      actorName: args.byUserName,
    }).catch(() => undefined);
  }
}

/** Existing non-void bill for an RFQ submission award (dedupe on re-approve). */
export async function findSupplierBillForRfqAward(
  rfqProjectId: string,
  rfqSubmissionId: string,
): Promise<SupplierBill | null> {
  const q = query(
    collection(db, BILLS),
    where('rfqProjectId', '==', rfqProjectId),
    where('rfqSubmissionId', '==', rfqSubmissionId),
  );
  const snap = await getDocs(q);
  const bills = snap.docs.map(mapBill).filter((b) => b.status !== 'void');
  return bills[0] ?? null;
}

/**
 * Any non-void CNC award bill for this RFQ + supplier org, under any
 * `rfqSubmissionId` key (`org:{id}` or a real submission id). Prefers a
 * terms-bearing row so Dorval-style client bills keep matching after the
 * server used to write a parallel org-keyed duplicate.
 */
export async function findSupplierBillForCncOrgAward(
  rfqProjectId: string,
  supplierOrgId: string,
): Promise<SupplierBill | null> {
  const q = query(
    collection(db, BILLS),
    where('rfqProjectId', '==', rfqProjectId),
    where('supplierOrgId', '==', supplierOrgId),
  );
  const snap = await getDocs(q);
  const bills = snap.docs
    .map(mapBill)
    .filter((b) => b.status !== 'void' && b.category === 'cnc' && b.supplierOrgId === supplierOrgId);
  const withTerms = bills.filter((b) => (b.paymentTerms?.installments?.length ?? 0) > 0);
  return withTerms[0] ?? bills[0] ?? null;
}

/** Existing non-void bill for an approved Project Hub quote file. */
export async function findSupplierBillForProjectFile(
  projectFileId: string,
): Promise<SupplierBill | null> {
  const q = query(collection(db, BILLS), where('projectFileId', '==', projectFileId));
  const snap = await getDocs(q);
  const bills = snap.docs.map(mapBill).filter((b) => b.status !== 'void');
  return bills[0] ?? null;
}

/** Void all active bills for a supplier org on an RFQ (CNC unapprove — per-org awards). */
export async function voidSupplierBillsForRfqOrg(args: {
  rfqProjectId: string;
  supplierOrgId: string;
  byUserId: string;
  byUserName: string;
}): Promise<void> {
  const q = query(
    collection(db, BILLS),
    where('rfqProjectId', '==', args.rfqProjectId),
    where('supplierOrgId', '==', args.supplierOrgId),
  );
  const snap = await getDocs(q);
  for (const bill of snap.docs.map(mapBill)) {
    if (bill.status === 'void' || bill.status === 'paid') continue;
    await updateSupplierBillStatus({
      bill,
      to: 'void',
      byUserId: args.byUserId,
      byUserName: args.byUserName,
      note: 'Award retracted',
    });
  }
}

/** Void the tracked bill when an RFQ award is retracted. */
export async function voidSupplierBillForRfqAward(args: {
  rfqProjectId: string;
  rfqSubmissionId: string;
  byUserId: string;
  byUserName: string;
}): Promise<void> {
  const bill = await findSupplierBillForRfqAward(args.rfqProjectId, args.rfqSubmissionId);
  if (!bill || bill.status === 'paid') return;
  await updateSupplierBillStatus({
    bill,
    to: 'void',
    byUserId: args.byUserId,
    byUserName: args.byUserName,
    note: 'Award retracted',
  });
}

function supplierBillContentType(file: File): string {
  const type = (file.type || '').toLowerCase();
  if (type === 'application/pdf' || type.startsWith('image/')) return type;
  const name = file.name.toLowerCase();
  if (name.endsWith('.pdf')) return 'application/pdf';
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  if (name.endsWith('.webp')) return 'image/webp';
  if (name.endsWith('.gif')) return 'image/gif';
  if (name.endsWith('.heic')) return 'image/heic';
  return type || 'application/octet-stream';
}

/** Upload an ad-hoc bill file to Storage; returns url + path. */
export async function uploadSupplierBillFile(
  file: File,
  byUserId: string,
): Promise<{ fileUrl: string; storagePath: string; fileName: string }> {
  await ensureCrmStorageAccessBeforeUpload();
  const safeName = file.name.replace(/[^\w.\-() ]+/g, '_').slice(0, 120);
  const storagePath = `supplier-bills/${byUserId}/${Date.now()}_${safeName}`;
  const fileRef = ref(storage, storagePath);
  await uploadBytes(fileRef, file, { contentType: supplierBillContentType(file) });
  const fileUrl = await getDownloadURL(fileRef);
  return { fileUrl, storagePath, fileName: safeName };
}

export { mapPaymentTerms, serializePaymentTerms };
